
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Plus, Edit, Trash, GripVertical } from 'lucide-react';
import { toast } from '@/lib/toast';
import { Lesson } from '@/models/Lesson';

interface LessonListProps {
  lessons: Lesson[];
  onAddLesson?: () => void;
  onEditLesson?: (lesson: Lesson) => void;
  onDeleteLesson?: (lessonId: string) => void;
  onToggleActive?: (lesson: Lesson, active: boolean) => void;
  onReorderLessons?: (reorderedLessons: Lesson[]) => void;
}

const LessonList = ({ 
  lessons, 
  onAddLesson, 
  onEditLesson, 
  onDeleteLesson, 
  onToggleActive, 
  onReorderLessons 
}: LessonListProps) => {
  const [draggedItem, setDraggedItem] = useState<Lesson | null>(null);
  const [draggedOverItem, setDraggedOverItem] = useState<Lesson | null>(null);

  const handleDelete = (lesson: Lesson) => {
    if (onDeleteLesson) {
      onDeleteLesson(lesson.id);
      toast.success(`"${lesson.name}" removed from your lessons`);
    }
  };

  const handleDragStart = (e: React.DragEvent, lesson: Lesson) => {
    setDraggedItem(lesson);
    e.dataTransfer.effectAllowed = 'move';
    // Required for Firefox to enable drag
    e.dataTransfer.setData('text/plain', lesson.id);
  };

  const handleDragOver = (e: React.DragEvent, lesson: Lesson) => {
    e.preventDefault();
    setDraggedOverItem(lesson);
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (draggedItem && draggedOverItem && draggedItem.id !== draggedOverItem.id && onReorderLessons) {
      const draggedIndex = lessons.findIndex(lesson => lesson.id === draggedItem.id);
      const targetIndex = lessons.findIndex(lesson => lesson.id === draggedOverItem.id);
      
      if (draggedIndex !== -1 && targetIndex !== -1) {
        const reorderedLessons = [...lessons];
        const [removed] = reorderedLessons.splice(draggedIndex, 1);
        reorderedLessons.splice(targetIndex, 0, removed);
        
        onReorderLessons(reorderedLessons);
      }
    }
    
    setDraggedItem(null);
    setDraggedOverItem(null);
  };

  const handleToggleActive = (lesson: Lesson, checked: boolean) => {
    if (onToggleActive) {
      onToggleActive(lesson, checked);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Your Lessons</h2>
        <Button onClick={onAddLesson} size="sm" className="gap-1">
          <Plus className="h-4 w-4" />
          <span>Add Lesson</span>
        </Button>
      </div>
      
      {lessons.length === 0 ? (
        <div className="text-center py-8 px-4 rounded-lg border border-dashed border-border">
          <p className="text-muted-foreground mb-4">No lessons added yet</p>
          <Button onClick={onAddLesson} variant="outline" size="sm" className="gap-1">
            <Plus className="h-4 w-4" />
            <span>Add your first lesson</span>
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {lessons.map(lesson => (
            <div 
              key={lesson.id}
              className={`flex items-center justify-between p-3 rounded-lg bg-card border border-border transition-all hover:shadow-md ${
                draggedItem?.id === lesson.id ? 'opacity-50' : ''
              } ${
                draggedOverItem?.id === lesson.id ? 'border-primary' : ''
              }`}
              draggable
              onDragStart={(e) => handleDragStart(e, lesson)}
              onDragOver={(e) => handleDragOver(e, lesson)}
              onDrop={handleDrop}
              onDragEnd={() => {
                setDraggedItem(null);
                setDraggedOverItem(null);
              }}
            >
              <div className="flex items-center">
                <div className="cursor-move mr-2 text-muted-foreground">
                  <GripVertical className="h-5 w-5" />
                </div>
                <div 
                  className="flex-1 cursor-pointer"
                  onClick={() => onEditLesson && onEditLesson(lesson)}
                >
                  <div className="flex items-center gap-2">
                    <span className={`font-medium ${!lesson.active ? 'text-muted-foreground' : ''}`}>
                      {lesson.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm text-muted-foreground">{lesson.words.length} words</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={lesson.active} 
                    onCheckedChange={(checked) => handleToggleActive(lesson, checked)}
                    aria-label={`Toggle ${lesson.name} active state`}
                  />
                </div>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-8 w-8"
                  onClick={() => onEditLesson && onEditLesson(lesson)}
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={() => handleDelete(lesson)}
                >
                  <Trash className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LessonList;
